import AppComponent from '../../../base/app-component.ts';
import type { UploadUiSpec } from '@/types/casse/upload-ui-spec.ts';
import {
  type CassaUploadState,
  accessoryKeyToRuolo,
  bindAccessorioEvents,
  buildUploadUrl,
  initAccessoriState,
  renderDropzone,
} from './bergamo-upload-shared.ts';

/**
 * wc-bergamo-ec-upload — UI upload per Bergamo Edilcassa (EC).
 *
 * Pattern: 1 dropzone multi-zip (con prefissi attesi, fra cui `iscritti_filca`)
 *          + N dropzone accessori sindacali esterni (FILLEA, FENEAL).
 *
 * Validazione client multi-zip: prima del trigger upload verifica che tutti i
 * prefissi in `expectedPrefixes` siano coperti dai nomi file in coda; se ok,
 * scatena `instance.upload()` via `queueMicrotask`.
 *
 * Riceve la `UploadUiSpec` via `data-options` JSON (vedi wc-cassa-upload).
 * Emette `cassa-upload-changed` ad ogni cambio stato (archivio/accessori).
 */
export class BergamoEcUpload extends AppComponent {
  static override componentName = 'wc-bergamo-ec-upload';
  protected override get templateId() { return 'bergamo-ec-upload'; }

  private state: CassaUploadState = {
    archivio: { ok: false, filename: '' },
    accessori: {},
  };

  protected override afterRender(): void {
    const spec = this.readSpec();
    if (!spec) return;

    this.state.accessori = initAccessoriState(spec.accessoryInputs);

    this.mountArchivioDropzone(spec);
    this.mountAccessoriDropzones(spec);
  }

  private readSpec(): UploadUiSpec | null {
    const raw = this.getAttribute('data-options');
    if (!raw) {
      console.warn('[wc-bergamo-ec-upload] data-options assente');
      return null;
    }
    try {
      return JSON.parse(raw) as UploadUiSpec;
    } catch {
      console.warn('[wc-bergamo-ec-upload] data-options non e\' JSON valido');
      return null;
    }
  }

  private mountArchivioDropzone(spec: UploadUiSpec): void {
    const container = this.$('[data-dropzone-container="archivio"]');
    if (!container) return;

    const codice = this.attributeString('codice', 'EC');
    const anno = this.attributeString('anno');
    const mese = this.attributeString('mese');
    const url = buildUploadUrl(codice, anno, mese, 'archivio');

    const el = renderDropzone(container, {
      url,
      accept: spec.acceptTypes.join(','),
      maxFiles: 10,
      send: 'queue',
      showUploadBtn: false,
    });
    if (!el) return;

    const expectedPrefixes = spec.expectedPrefixes ?? [];

    el.addEventListener('added.wx.dropzone', () => {
      this.handlePrefixValidation(el, expectedPrefixes);
    });

    el.addEventListener('complete.wx.dropzone', (e: Event) => {
      const detail = (e as CustomEvent).detail as { success?: number } | undefined;
      const success = detail?.success ?? 0;
      if (success > 0) {
        this.state.archivio = {
          ok: true,
          filename: `${success} file .zip caricati`,
        };
      } else {
        this.state.archivio = { ok: false, filename: '' };
      }
      this.emitChange();
    });
  }

  private handlePrefixValidation(el: HTMLElement, expectedPrefixes: string[]): void {
    const hintEl = this.$('[data-archivio-hint]') as HTMLElement | null;
    if (expectedPrefixes.length === 0) return;

    const instance = Weext.Dropzone.getInstance(el);
    if (!instance) return;

    const names = instance.queuedFiles.map(f => f.file.name.toLowerCase());
    const found = expectedPrefixes.filter(prefix =>
      names.some(n => n.startsWith(prefix))
    );
    const missing = expectedPrefixes.filter(prefix =>
      !names.some(n => n.startsWith(prefix))
    );

    if (hintEl) {
      if (missing.length === 0) {
        hintEl.textContent = 'Tutti i file necessari trovati.';
        hintEl.className = 'small text-success mt-2';
      } else {
        hintEl.textContent = `Trovati: ${found.join(', ') || 'nessuno'}. Mancano: ${missing.join(', ')}`;
        hintEl.className = 'small text-warning mt-2';
      }
    }

    if (missing.length === 0 && !instance.isUploading) {
      queueMicrotask(() => instance.upload());
    }
  }

  private mountAccessoriDropzones(spec: UploadUiSpec): void {
    if (!spec.accessoryInputs) return;
    const codice = this.attributeString('codice', 'EC');
    const anno = this.attributeString('anno');
    const mese = this.attributeString('mese');

    for (const [key, input] of Object.entries(spec.accessoryInputs)) {
      const container = this.$(`[data-dropzone-container="${key}"]`);
      if (!container) continue;

      const ruolo = accessoryKeyToRuolo(key);
      const url = buildUploadUrl(codice, anno, mese, ruolo);

      const el = renderDropzone(container, {
        url,
        accept: input.acceptTypes.join(','),
        maxFiles: 1,
        send: 'auto',
        showUploadBtn: true,
      });
      if (!el) continue;

      bindAccessorioEvents(el, key, this.state, () => this.emitChange());
    }
  }

  private emitChange(): void {
    this.dispatch('cassa-upload-changed', {
      archivio: this.state.archivio,
      accessori: { ...this.state.accessori },
    });
  }
}
